// Mantine
import { MRT_ColumnDef } from 'mantine-react-table';
import {
  ActionIcon,
  Badge,
  Menu,
  Select,
  Text,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronDown, IconDotsVertical } from '@tabler/icons-react';

// Refine
import { useTranslation } from '@refinedev/core';

// Interfaces
import { Site, User } from 'interfaces';

// Utils
import {
  compareDateAsc,
  formatDateOnly,
  formatDateTime,
  nowISO,
  parseDateTime,
} from 'utils/dateUtils';
import { getUserFullName } from 'hooks/useGetUsersDisplayInfo';

// Components
import { UploadStatusBadge } from 'pages/uploads/components/UploadStatusBadge';
import { DataBadge } from 'components/common/DataBadge';
import {
  getUploadStatusBadgeProps,
  getUploadStatusOptions,
  isUploadStatus,
} from 'pages/uploads/components/uploadStatus';
import { IntakeItem } from '@billing/validation';

const formatName = ({
  firstName,
  lastName,
  title,
}: {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
}) => {
  const name = [title, firstName, lastName]
    .filter((part) => part && part.trim())
    .join(' ')
    .trim();
  return name || 'N/A';
};

export const formatLinkedClaimsServiceDateRange = (
  claims: IntakeItem['claims']
) => {
  const serviceDates = (claims ?? [])
    .map((claim) => parseDateTime(claim?.serviceStart))
    .filter((dt) => dt.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  if (!serviceDates.length) return 'N/A';

  const earliest = serviceDates[0];
  const latest = serviceDates[serviceDates.length - 1];
  const earliestLabel = formatDateOnly(earliest.toISO());
  const latestLabel = formatDateOnly(latest.toISO());

  if (!earliestLabel || !latestLabel) return 'N/A';
  if (earliest.hasSame(latest, 'day')) return earliestLabel;
  return `${earliestLabel} - ${latestLabel}`;
};

interface UploadColumnsProps {
  assignableUsers?: User[];
  onAssign?: (item: IntakeItem, assigneeUserID: string | null) => void;
  pendingAssignments?: Record<string, string | null>;
  savingAssignments?: Set<string>;
  isLoadingUsers?: boolean;
  currentUserID?: string | null;
  canAssign?: boolean;
  onStatusChange?: (item: IntakeItem, status: string) => void;
  pendingStatuses?: Record<string, string | null>;
  savingStatuses?: Set<string>;
  canModify?: boolean;
  onViewIntake?: (item: IntakeItem) => void;
  sites: Site[];
}

export const UploadColumns = ({
  assignableUsers = [],
  onAssign,
  pendingAssignments = {},
  savingAssignments = new Set<string>(),
  isLoadingUsers = false,
  currentUserID,
  canAssign = false,
  onStatusChange,
  pendingStatuses = {},
  savingStatuses = new Set<string>(),
  canModify = false,
  onViewIntake,
  sites = [],
}: UploadColumnsProps) => {
  const { translate } = useTranslation();
  const theme = useMantineTheme();
  const userOptions = assignableUsers.map((user) => ({
    value: user.id,
    label: getUserFullName(user),
  }));
  const statusOptions = getUploadStatusOptions(translate);
  const assigneeFilterOptions = [
    {
      value: 'unassigned',
      label: translate('pages:uploads.table.unassigned', 'Unassigned'),
    },
    ...userOptions,
  ];

  const columns: MRT_ColumnDef<IntakeItem>[] = [
    {
      id: 'actions',
      columnDefType: 'display',
      header: translate('pages:uploads.table.actions', 'Actions'),
      size: 75,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enablePinning: true,
      Cell: ({ row }) => {
        return (
          <Menu shadow="md" width={180} position="right-start" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label={translate(
                  'pages:uploads.table.actionsAria',
                  'Intake actions'
                )}
              >
                <IconDotsVertical size={18} stroke={1.5} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                {translate('pages:uploads.table.actions', 'Actions')}
              </Menu.Label>
              <Menu.Item onClick={() => onViewIntake?.(row.original)}>
                {translate('pages:uploads.table.openDetails', 'Open Details')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        );
      },
    },
    {
      accessorKey: 'intakeNumber',
      header: translate('pages:uploads.table.intakeNumber', 'Intake #'),
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: 'text',
      filterFn: 'contains',
      mantineFilterTextInputProps: {
        placeholder: translate(
          'pages:uploads.table.searchIntakeNumber',
          'Search intake #'
        ),
      },
      Cell: ({ cell }) => {
        const value = cell.getValue() as number | string | null | undefined;
        return (
          <Text
            size="sm"
            fw={500}
            lineClamp={1}
            title={value ? `${value}` : 'N/A'}
          >
            {value ?? 'N/A'}
          </Text>
        );
      },
    },
    {
      id: 'provider',
      header: translate('pages:uploads.table.provider', 'Provider'),
      enableSorting: false,
      accessorFn: (row) =>
        formatName({
          title: row.provider?.title,
          firstName: row.provider?.firstName,
          lastName: row.provider?.lastName,
        }),
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Text fw={400}>{value || 'N/A'}</Text>;
      },
      size: 180,
    },
    {
      accessorKey: 'createdAt',
      header: translate('pages:uploads.table.createdAt', 'Created At'),
      accessorFn: (row) => formatDateTime(row.createdAt, row.site?.timezone),
      Cell: ({ cell }) => <Text>{cell.getValue() as string}</Text>,
      sortingFn: (rowA, rowB, columnId) =>
        compareDateAsc(
          rowA.getValue<string>(columnId),
          rowB.getValue<string>(columnId)
        ),

      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
        max: nowISO(),
      },
      filterVariant: 'date-range',
    },
    {
      id: 'linkedClaims',
      header: translate('pages:uploads.table.linkedClaims', 'Linked Claims'),
      accessorFn: (row) => row.claims?.length || 0,
      enableSorting: false,
      enableColumnFilter: false,
      grow: false,
      mantineTableHeadCellProps: {
        style: {
          width: '1%',
          whiteSpace: 'nowrap',
        },
      },
      mantineTableBodyCellProps: {
        style: {
          width: '1%',
          whiteSpace: 'nowrap',
        },
      },
      Cell: ({ row }) => {
        const claimCount = row.original.claims?.length || 0;
        return <DataBadge>{claimCount}</DataBadge>;
      },
    },
    {
      id: 'claims.serviceStart',
      header: translate(
        'pages:uploads.table.serviceDateRange',
        'Service Date Range'
      ),
      accessorFn: (row) => formatLinkedClaimsServiceDateRange(row.claims),
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: 'date-range',
      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
        max: nowISO(),
      },
      Cell: ({ cell }) => <Text>{(cell.getValue() as string) || 'N/A'}</Text>,
    },
    {
      accessorKey: 'status',
      header: translate('pages:uploads.table.status', 'Status'),
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: 'multi-select',
      mantineFilterMultiSelectProps: {
        data: statusOptions,
        placeholder: translate(
          'pages:uploads.table.filterStatus',
          'Filter status'
        ),
        searchable: true,
        clearable: true,
        clearButtonProps: {
          style: { display: 'none' },
        },
      },
      Cell: ({ row }) => {
        const rowId = row.original.id;
        const hasPending = Object.prototype.hasOwnProperty.call(
          pendingStatuses,
          rowId
        );
        const rawValue = hasPending
          ? pendingStatuses[rowId]
          : row.original.status ?? null;
        const statusKey = isUploadStatus(rawValue) ? rawValue : null;
        const canInteract = Boolean(canModify);
        const disabled =
          !onStatusChange || !canInteract || savingStatuses.has(rowId);

        if (!canInteract || !onStatusChange || disabled) {
          return <UploadStatusBadge status={rawValue} />;
        }

        const currentLabel =
          statusOptions.find((option) => option.value === statusKey)?.label ??
          rawValue ??
          translate('pages:uploads.table.selectStatus', 'Select status');
        const badgeProps = getUploadStatusBadgeProps(
          statusKey ?? rawValue ?? '',
          theme
        );

        return (
          <Menu position="bottom-start" shadow="sm" withinPortal>
            <Menu.Target>
              <UnstyledButton
                style={{
                  display: 'inline-flex',
                  cursor: 'pointer',
                }}
              >
                <Badge
                  radius="sm"
                  size="md"
                  rightSection={
                    <IconChevronDown size={14} stroke={1.5} aria-hidden />
                  }
                  {...badgeProps}
                >
                  {currentLabel}
                </Badge>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {statusOptions.map((option) => {
                const optionBadgeProps = getUploadStatusBadgeProps(
                  option.value,
                  theme
                );
                return (
                  <Menu.Item
                    key={option.value}
                    onClick={() => {
                      if (option.value === statusKey) return;
                      onStatusChange(row.original, option.value);
                    }}
                    disabled={option.value === statusKey}
                  >
                    <Badge radius="sm" size="md" {...optionBadgeProps}>
                      {option.label}
                    </Badge>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        );
      },
      size: 120,
    },
    {
      id: 'assigneeUserID',
      accessorKey: 'assigneeUserID',
      header: translate('pages:uploads.table.assignedClerk', 'Assigned Clerk'),
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: assigneeFilterOptions,
        placeholder: translate(
          'pages:uploads.table.filterClerk',
          'Filter clerk'
        ),
        searchable: true,
        clearable: true,
      },
      Cell: ({ row }) => {
        const rowId = row.original.id;
        const hasPending = Object.prototype.hasOwnProperty.call(
          pendingAssignments,
          rowId
        );
        const currentValue = hasPending
          ? pendingAssignments[rowId]
          : row.original.assigneeUserID ?? null;
        const hasSelfOption = Boolean(
          currentUserID &&
            userOptions.some((option) => option.value === currentUserID)
        );
        const isUnassigned = !currentValue;
        const isSelfAssigned =
          Boolean(currentUserID) && currentValue === currentUserID;
        const canSelfAssign = hasSelfOption && (isUnassigned || isSelfAssigned);
        const canAssignAny = Boolean(canAssign);
        const canInteract = canAssignAny || canSelfAssign;
        const disabled =
          !onAssign ||
          !canInteract ||
          isLoadingUsers ||
          savingAssignments.has(rowId);
        const selectOptions = canAssignAny
          ? userOptions
          : canSelfAssign
          ? userOptions.filter((option) => option.value === currentUserID)
          : userOptions;

        return (
          <Select
            data={selectOptions}
            value={currentValue}
            onChange={(value) => {
              if (!onAssign) return;
              const nextValue = value ?? null;
              if (!canAssignAny) {
                if (!currentUserID) return;
                if (nextValue !== currentUserID) return;
                if (currentValue && currentValue !== currentUserID) return;
              }
              if (nextValue === currentValue) return;
              onAssign(row.original, nextValue);
            }}
            placeholder={
              isLoadingUsers
                ? translate(
                    'pages:uploads.table.loadingUsers',
                    'Loading users...'
                  )
                : translate('pages:uploads.table.unassigned', 'Unassigned')
            }
            searchable
            clearable={canAssignAny}
            disabled={disabled}
            size="xs"
          />
        );
      },
    },
    {
      accessorKey: 'siteID',
      header: 'Site',
      Cell: ({ cell }) => (
        <Badge color="teal" variant="filled">
          {sites.find((site) => site.id === cell.getValue())?.name}
        </Badge>
      ),
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
  ];

  return columns;
};
